import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { IconCheck, IconChevronDown } from './icons.js';

/** The primitives as components; classes live in styles/controls.css. */

export function Button({ kind, busy, disabled, onClick, children, label, type = 'button' }: {
  kind?: 'primary' | 'ghost' | 'danger' | 'icon' | 'sm';
  busy?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
  label?: string;
  type?: 'button' | 'submit';
}) {
  const cls = ['button', kind && `button--${kind}`].filter(Boolean).join(' ');
  return (
    <button type={type} className={cls} aria-disabled={disabled || busy || undefined} aria-busy={busy || undefined}
      aria-label={label} onClick={() => { if (!disabled && !busy) onClick?.(); }}>
      {busy ? <span className="spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export function Seg<T extends string>({ value, options, onChange, label }: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value}
          className={o.value === value ? 'seg--on' : undefined} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

export function Switch({ on, onChange, label, disabled }: { on: boolean; onChange: (on: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
      className={`switch-pill${on ? ' switch-pill--on' : ''}`} onClick={(e) => { e.stopPropagation(); onChange(!on); }} />
  );
}

export type Tone = 'success' | 'warn' | 'bad' | 'brand' | 'neutral';
export function Chip({ tone, slot, children }: { tone: Tone; slot?: boolean; children: ReactNode }) {
  return <span className={`chip${tone !== 'neutral' ? ` chip--${tone}` : ''}${slot ? ' chip--slot' : ''}`}>{children}</span>;
}

/** Our select: a box and a menu of options, each with a name and a note; keyboard as a listbox. */
export function Choice<T extends string>({ value, options, onChange, label, id }: {
  value: T;
  options: { value: T; label: string; note?: string }[];
  onChange: (value: T) => void;
  label: string;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(() => Math.max(0, options.findIndex((o) => o.value === value)));
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  const current = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const pick = (i: number) => { const o = options[i]; if (o) onChange(o.value); setOpen(false); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (open) pick(active); else setOpen(true);
    }
  };

  return (
    <div className="choice" ref={root}>
      <button type="button" id={id} className="select" aria-haspopup="listbox" aria-expanded={open} aria-controls={listId}
        aria-label={label} onClick={() => setOpen((o) => !o)} onKeyDown={onKey}
        aria-activedescendant={open ? `${listId}-${active}` : undefined}>
        <span>{current?.label}</span>
        <IconChevronDown />
      </button>
      {open ? (
        <ul className="menu" role="listbox" id={listId} aria-label={label}>
          {options.map((o, i) => (
            <li key={o.value} id={`${listId}-${i}`} role="option" aria-selected={o.value === value}
              className={`menu__option${i === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); pick(i); }}>
              <span className="menu__name">{o.label}</span>
              <span className="menu__check"><IconCheck size="sm" /></span>
              {o.note ? <span className="menu__note">{o.note}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function ToggleChips<T extends string>({ values, options, onChange, label }: {
  values: T[];
  options: { value: T; label: string }[];
  onChange: (values: T[]) => void;
  label: string;
}) {
  return (
    <div className="toggle-chips" role="group" aria-label={label}>
      {options.map((o) => {
        const on = values.includes(o.value);
        return (
          <button key={o.value} type="button" className="toggle-chip" aria-pressed={on}
            onClick={() => onChange(on ? values.filter((v) => v !== o.value) : options.map((x) => x.value).filter((v) => v === o.value || values.includes(v)))}>
            <IconCheck size="sm" />{o.label}
          </button>
        );
      })}
    </div>
  );
}
